import { cfetchWithAbort } from '../../utils/cfetchWithAbort.ts';
import { IFS_API } from '../../config/apiConstants.ts';

type BasicCredentials = {
	apiKey: string;
	appId: string;
};

type AuthWindowSize = {
	width: number;
	height: number;
};

export type AuthData = {
	apiKey: string;
	appId: string;
	oauthToken: string;
};

export const AUTH_WINDOW_SIZE: AuthWindowSize = {
	width: 600,
	height: 600,
};

class GoogleAuthenticator {
	public authWindow: Window | null = null;
	private popupMessageHandler: ((evt) => any) | null = null;

	async getAuthData(): Promise<AuthData> {
		const [oauthToken, { appId, apiKey }] = await Promise.all([
			this.obtainOauthToken(),
			this.obtainBasicCredentials(),
		]);

		return {
			apiKey,
			appId,
			oauthToken,
		};
	}

	async obtainBasicCredentials(): Promise<BasicCredentials> {
		const { data } = await cfetchWithAbort(`${IFS_API}/google/secrets`, {
			mode: 'cors',
			credentials: 'include',
		}).then((response) => response.json());

		return data;
	}

	async obtainOauthToken(): Promise<string> {
		// window.open should not be called in async code to prevent popup blocking in Safari, Firefox
		this.authWindow = window.open(
			'',
			'auth',
			`width=${AUTH_WINDOW_SIZE.width},height=${
				AUTH_WINDOW_SIZE.height
			},toolbar=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,copyhistory=no,top=${
				(screen.height - AUTH_WINDOW_SIZE.height) / 2
			},left=${(screen.width - AUTH_WINDOW_SIZE.width) / 2}`,
		);

		const oauthToken = await Promise.race([this.handleCloseAuthWindow(), this.handleGrantAccess()]);

		window.removeEventListener('message', this.popupMessageHandler!);
		this.popupMessageHandler = null;
		this.authWindow = null;

		return oauthToken;
	}

	handleCloseAuthWindow(): Promise<string> {
		return new Promise((resolve) => {
			const self = this;
			(function checkAuthWindow() {
				if (self.authWindow && !self.authWindow.closed) {
					return setTimeout(checkAuthWindow, 100);
				}
				resolve('');
			})();
		});
	}

	handleGrantAccess(): Promise<string> {
		return new Promise((resolve) => {
			this.popupMessageHandler = this.handleMessage(resolve);
			window.addEventListener('message', this.popupMessageHandler);
			// eslint-disable-next-line no-restricted-syntax
			this.authWindow!.location.href = `${IFS_API}/google/login`;
		});
	}

	handleMessage =
		(callback: any) =>
		(evt: any): void => {
			if (typeof evt.data === 'string' && evt.data.startsWith('ifs_google_access_token')) {
				if (this.authWindow && !this.authWindow.closed) {
					this.authWindow.close();
				}
				callback(evt.data.split(':')[1]);
			}
		};
}

export const GoogleAuth: GoogleAuthenticator = new GoogleAuthenticator();
